import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { PageList } from '~/layout';
import { Location } from '~/types';
import CardsList from './components/CardsList';
import TerminalCreate from './components/TerminalCreate';
import Filters from './components/Filters';
import TerminalShow from './components/TerminalShow';
import TerminalsTable from './components/TerminalsTable';
import { usePath, usePermissions } from '~/hooks';
import { NoPermission } from '~/components';
import { useTerminalsQuery } from '~/hooks/terminals';
import { useAllLocations } from '~/hooks/locations';
import { detachKeys } from '~/utils';
import { useMemo } from 'react';
import { useAccountsByIdsQuery } from '~/hooks/accounts';

const TerminalsPage: FC<RouteComponentProps> = () => {
  const { t } = useTranslation('terminals');
  const { data, isLoading, isFetching, total } = useTerminalsQuery();
  const allAccountIds =
    data?.flatMap((t) => (Array.isArray(t.accountIds) ? t.accountIds : [])) || [];
  const uniqueIds = useMemo(
    () => Array.from(new Set(allAccountIds.filter(Boolean))),
    [allAccountIds]
  );
  const { data: accountsData, isLoading: isAccountsDataLoading } = useAccountsByIdsQuery(uniqueIds);
  const accounts =
    accountsData?.data && Array.isArray(accountsData?.data) ? accountsData?.data : [];
  const accountMap = useMemo(() => new Map(accounts.map((acc: any) => [acc.id, acc])), [accounts]);
  const { data: allLocations } = useAllLocations();
  const [locationsById] = detachKeys<Location>(allLocations);

  const { changePath } = usePath();

  const hasAccessList = usePermissions('business.terminal.list');
  if (!hasAccessList) return <NoPermission />;
  return (
    <>
      <TerminalShow />
      <PageList
        header={{
          heading: t('terminals'),
          searchPlaceholder: t('searchPlaceholder'),
          onSearch: (name: string) => changePath({ filter: { name } }),
          queryParam: 'name',
          isFetching,
        }}
        filters={<Filters />}
        table={
          <TerminalsTable
            data={data}
            total={total}
            loading={isLoading}
            locationsById={locationsById}
            accountMap={accountMap}
            accountsDataLoading={isAccountsDataLoading}
          />
        }
        cards={
          <CardsList
            data={data}
            total={total}
            loading={isLoading}
            locationsById={locationsById}
            accountMap={accountMap}
            accountsDataLoading={isAccountsDataLoading}
          />
        }
        add={<TerminalCreate />}
        defaultDisplayMode="cards"
      />
    </>
  );
};

export default TerminalsPage;
